import _cloneDeep from 'lodash-es/cloneDeep';
import { normalize } from 'normalizr';

import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import {
  createStorageRequest,
  type FilesStorageSchemaPatched,
  updateStorageRequest,
} from '@iconik-io/iconik-api/src/schema_patched/files_storages';
import {
  type FilesHttpGetStoragesByStorageIdVerificationsAccessResponse,
  type FilesHttpGetStoragesByStorageIdVerificationsPermissionsResponse,
  type FilesStoragePermissionsSchema,
  httpDeleteStoragesByStorageId,
  httpDeleteStoragesByStorageIdDefault,
  httpGetStorages,
  httpGetStoragesByStorageIdVerificationsAccess,
  httpGetStoragesByStorageIdVerificationsPermissions,
  httpPostStoragesByStorageIdDefault,
  httpPostStoragesByStorageIdScan,
  httpPostStoragesVerificationsAccess,
  httpPostStoragesVerificationsPermissions,
} from '@iconik-io/iconik-api/src/services/files';
import { entitySchemas } from '@iconik-io/shared-lib/src/redux/entities/schemas';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';
import type { StorageMethod } from '@iconik-io/shared-lib/src/utils/constants/storages';
import { getSortQuery } from '@iconik-io/shared-lib/src/utils/sort/get_sort_query';

import {
  isCloudOrIsgStorage,
  isCloudStorage,
} from '../../../components/admin_storage_form_fields/utils';
import { ADMIN_STORAGE_PAGE_ROUTE } from '../../../constants/routes';
import { SUPPORTED_VERIFY_METHODS } from '../../../constants/storages';
import type { AppDispatch, GetState } from '../../../store/types';
import { prepareQueryParameter } from '../../../utils/search_utils/search_utils';
import { setGettingStartedCheck } from '../../app/slice';
import {
  type CommonListGetParams,
  wrapCommonCreate,
  wrapCommonDelete,
  wrapCommonGetList,
} from '../../common_list/thunks';
import { historyPush } from '../../history/actions';
import { errorMessage, successMessage } from '../../messages/thunks';

export function getStorages(
  params: CommonListGetParams = {},
  debounce?: boolean,
) {
  return (dispatch: AppDispatch, getState: GetState) =>
    wrapCommonGetList({
      prefix: 'admin_storages',
      dispatch,
      params,
      debounce,
      getListPromise: (listParams) => {
        const state = getState();
        const pageState = state.pages.adminStorages;
        const filtersPanelState = state.panelsState.adminStoragesFilters;
        return dispatch(
          httpGetStorages({
            sort: getSortQuery(pageState.sort),
            page: listParams.page,
            per_page: 50,
            ...filtersPanelState.filterValues,
            ...filtersPanelState.facetValues,
            query: prepareQueryParameter(filtersPanelState.query, {
              keepSpaceAtEnd: true,
            }),
          }),
        );
      },
    });
}

export function deleteStoragesByIds(ids: string[]) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    wrapCommonDelete({
      prefix: 'admin_storages',
      dispatch,
      ids,
      deletePromise: Promise.all(
        ids.map((id) => dispatch(httpDeleteStoragesByStorageId(id))),
      ),
    }).then(() => {
      const state = getState();
      if (state.routeParams.route.startsWith(ADMIN_STORAGE_PAGE_ROUTE)) {
        dispatch(historyPush('/admin/storages'));
      }
    });
}

export type AccessPermissionsState = {
  storageAccess?: FilesHttpGetStoragesByStorageIdVerificationsAccessResponse;
  storagePermissions?: FilesHttpGetStoragesByStorageIdVerificationsPermissionsResponse;
  checkPermissionsAndAccessError?: number;
  storageId?: string;
};

function checkExistingStoragePermissions(storageId: string) {
  return (dispatch: AppDispatch): Promise<AccessPermissionsState> =>
    dispatch(httpGetStoragesByStorageIdVerificationsPermissions(storageId))
      .then((response) => ({
        storageId,
        storagePermissions: response.body,
      }))
      .catch((error: EndpointError) => ({
        storageId,
        checkPermissionsAndAccessError: error.response?.status,
      }));
}

function checkProposedStoragePermissions(data: FilesStoragePermissionsSchema) {
  return (dispatch: AppDispatch): Promise<AccessPermissionsState> =>
    dispatch(httpPostStoragesVerificationsPermissions(data))
      .then((response) => ({
        storagePermissions: response.body,
      }))
      .catch((error: EndpointError) => ({
        checkPermissionsAndAccessError: error.response?.status,
      }));
}

export function checkExistingStorageAccess(
  storageId: string,
  method: StorageMethod,
) {
  return async (dispatch: AppDispatch): Promise<AccessPermissionsState> => {
    if (method && !SUPPORTED_VERIFY_METHODS.includes(method)) {
      return {
        checkPermissionsAndAccessError: 501,
      };
    }

    // For Azure do the permissions check only as the access check is implemented in there
    if (method === 'AZURE') {
      return dispatch(checkExistingStoragePermissions(storageId)).catch(
        (error) => {
          if (error instanceof EndpointError) {
            return {
              storageId,
              checkPermissionsAndAccessError: error.response?.status,
            };
          }
          throw error;
        },
      );
    }

    return dispatch(httpGetStoragesByStorageIdVerificationsAccess(storageId))
      .then(async (response) => {
        let permissionsState: AccessPermissionsState = {
          storageId,
        };
        if (response.body.access_check) {
          permissionsState = await dispatch(
            checkExistingStoragePermissions(storageId),
          );
        }
        return {
          ...permissionsState,
          storageAccess: response.body,
        };
      })
      .catch((error) => {
        if (error instanceof EndpointError) {
          return {
            storageId,
            checkPermissionsAndAccessError: error.response?.status,
          };
        }
        throw error;
      });
  };
}

export function checkProposedStorageAccess(
  accessSchema: FilesStoragePermissionsSchema,
) {
  const method = accessSchema.method;

  return async (dispatch: AppDispatch): Promise<AccessPermissionsState> => {
    if (method && !SUPPORTED_VERIFY_METHODS.includes(method)) {
      return {
        checkPermissionsAndAccessError: 501,
      };
    }

    try {
      if (method === 'AZURE') {
        return await dispatch(checkProposedStoragePermissions(accessSchema));
      }

      const accessResponse = await dispatch(
        httpPostStoragesVerificationsAccess(accessSchema),
      );

      let permissionsState: AccessPermissionsState = {};
      if (accessResponse.body.access_check) {
        permissionsState = await dispatch(
          checkProposedStoragePermissions(accessSchema),
        );
      }

      return {
        ...permissionsState,
        storageAccess: accessResponse.body,
      };
    } catch (error) {
      if (error instanceof EndpointError) {
        return {
          checkPermissionsAndAccessError: error.response?.status,
        };
      }
      throw error;
    }
  };
}

export function updateStorage(
  storageId: string,
  data: Partial<FilesStorageSchemaPatched>,
) {
  return (
    dispatch: AppDispatch,
    getState: GetState,
  ): Promise<AccessPermissionsState> => {
    const storage = getState().entities.storages[storageId];
    const newData = _cloneDeep(data);

    if (newData.settings) {
      // Make sure that the method is set otherwise isCloudOrIsgStorage returns false.
      newData.method = storage.method;
      if (isCloudOrIsgStorage(newData)) {
        if (newData.settings.metadata_view_id === '') {
          newData.settings.metadata_view_id = null;
        }
        if (newData.settings.acl_template_id === '') {
          newData.settings.acl_template_id = null;
        }
        if (newData.settings.access_group_id === '') {
          newData.settings.access_group_id = null;
        }
        if (newData.settings.folder_name_tags_metadata_view_id === '') {
          newData.settings.folder_name_tags_metadata_view_id = null;
        }
      }
    }

    if (Object.keys(data).length === 0) {
      return dispatch(checkExistingStorageAccess(storage.id, storage.method));
    }
    return dispatch(updateStorageRequest(storageId, newData)).then((response) =>
      dispatch(checkExistingStorageAccess(storageId, response.body.method)),
    );
  };
}

export function createStorage(data: FilesStorageSchemaPatched) {
  return (dispatch: AppDispatch) => {
    let storage: FilesStorageSchemaPatched = data;
    if (isCloudStorage(data)) {
      // Spreading the storage drops the pairing between `method` and the
      // settings that go with it, which the rebuilt object still holds.
      // biome-ignore lint/plugin: see above.
      storage = {
        ...data,
        settings: {
          ...data.settings,
          // the backend requires at least empty string for path
          path: data.settings.path ?? '',
        },
      } as FilesStorageSchemaPatched;
    }

    dispatch(
      setGettingStartedCheck({ name: 'hasConnectedStorage', value: true }),
    );

    return wrapCommonCreate({
      prefix: 'admin_storages',
      dispatch,
      createPromise: dispatch(createStorageRequest(storage)),
    });
  };
}

export function scanStorage(id: string) {
  return (dispatch: AppDispatch, getState: GetState) =>
    dispatch(httpPostStoragesByStorageIdScan(id, {})).then(() => {
      // Manually change the storage scanner_status
      const storage = getState().entities.storages[id];
      const updatedStorage = {
        ...storage,
        scanner_status: 'ACTIVE',
      };
      const normalizedResult = normalize(
        updatedStorage,
        entitySchemas.storages,
      );
      dispatch(updateEntities(normalizedResult.entities));
      dispatch(
        successMessage({
          text: `${storage.name} will be scanned`,
        }),
      );
    });
}

export function setDefaultStorage(storageId: string, purpose: string) {
  return (dispatch: AppDispatch) =>
    dispatch(httpPostStoragesByStorageIdDefault(storageId)).then(() => {
      dispatch(
        successMessage({ text: `Set as Default Storage for ${purpose}` }),
      );
    });
}

export function deleteDefaultStorage(storageId: string, purpose: string) {
  return (dispatch: AppDispatch) =>
    dispatch(httpDeleteStoragesByStorageIdDefault(storageId)).then(() => {
      dispatch(
        errorMessage({ text: `Deleted as Default Storage for ${purpose}` }),
      );
    });
}
